import React, { useState } from 'react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { LeadNote } from '../../types/lead';
import Button from '../UI/Button';
import ConfirmationDialog from '../UI/ConfirmationDialog';

interface LeadNotesListProps {
  notes: LeadNote[];
  loading?: boolean;
  currentUserId?: string | null;
  isAdmin?: boolean;
  onAddNote: (body: string) => Promise<void>;
  onUpdateNote: (noteId: string, body: string) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
}

const NOTE_INPUT =
  'w-full min-h-[88px] rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none';

const formatNoteDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const LeadNotesList: React.FC<LeadNotesListProps> = ({
  notes,
  loading,
  currentUserId,
  isAdmin = false,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
}) => {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [actionNoteId, setActionNoteId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadNote | null>(null);

  const canModify = (note: LeadNote) =>
    isAdmin || (currentUserId && note.createdBy === currentUserId);

  const handleSave = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onAddNote(draft.trim());
      setDraft('');
      toast.success('Note saved');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note: LeadNote) => {
    setEditingId(note.id);
    setEditDraft(note.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const handleUpdate = async (noteId: string) => {
    if (!editDraft.trim()) return;
    setActionNoteId(noteId);
    try {
      await onUpdateNote(noteId, editDraft.trim());
      setEditingId(null);
      setEditDraft('');
      toast.success('Note updated');
    } catch {
      toast.error('Failed to update note');
    } finally {
      setActionNoteId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionNoteId(deleteTarget.id);
    try {
      await onDeleteNote(deleteTarget.id);
      if (editingId === deleteTarget.id) cancelEdit();
      toast.success('Note deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete note');
    } finally {
      setActionNoteId(null);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Loading notes...</div>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Composer — fixed at top of notes panel */}
      <div className="shrink-0 rounded-xl border border-border bg-muted/10 p-3">
        <label htmlFor="new-lead-note" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Add note
        </label>
        <textarea
          id="new-lead-note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a note about this lead..."
          rows={3}
          autoComplete="off"
          className={NOTE_INPUT}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void handleSave();
          }}
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">Ctrl+Enter to save</span>
          <Button size="sm" onClick={() => void handleSave()} disabled={saving || !draft.trim()}>
            {saving ? 'Saving...' : 'Save Note'}
          </Button>
        </div>
      </div>

      {/* Notes list — scrollable */}
      <div className="mt-4 min-h-0 flex-1">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Notes {notes.length > 0 && `(${notes.length})`}
          </h4>
        </div>

        {notes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-10 text-center">
            <p className="text-sm text-muted-foreground">No notes yet.</p>
            <p className="mt-1 text-xs text-muted-foreground">Add the first note above.</p>
          </div>
        ) : (
          <div className="space-y-2.5 pb-2">
            {notes.map((note) => {
              const isEditing = editingId === note.id;
              const isBusy = actionNoteId === note.id;
              const showActions = canModify(note);
              const wasEdited = note.updatedAt !== note.createdAt;

              return (
                <article
                  key={note.id}
                  className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
                >
                  {isEditing ? (
                    <div className="p-3 space-y-2">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={4}
                        autoComplete="off"
                        className={NOTE_INPUT}
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outlined" onClick={cancelEdit} disabled={isBusy}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => void handleUpdate(note.id)}
                          disabled={isBusy || !editDraft.trim()}
                        >
                          {isBusy ? 'Saving...' : 'Save changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/15 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-foreground">
                            {note.createdByName?.trim() || 'Unassigned'}
                          </p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {formatNoteDate(note.createdAt)}
                            {wasEdited && ' · Edited'}
                          </p>
                        </div>
                        {showActions && (
                          <div className="flex shrink-0 items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => startEdit(note)}
                              disabled={isBusy}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                              aria-label="Edit note"
                              title="Edit"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(note)}
                              disabled={isBusy}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                              aria-label="Delete note"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="px-3 py-3">
                        <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">
                          {note.body}
                        </p>
                      </div>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDeleteConfirm()}
        title="Delete note?"
        message="This note will be permanently removed from the lead. This action cannot be undone."
        confirmText="Delete note"
        cancelText="Cancel"
        isLoading={!!deleteTarget && actionNoteId === deleteTarget.id}
        variant="destructive"
      />
    </div>
  );
};

export default LeadNotesList;
